import { Component, OnInit, inject, signal } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  Validators,
  ReactiveFormsModule,
} from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';

import { MessageService } from 'primeng/api';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { ProgressSpinnerModule } from 'primeng/progressspinner';

import {
  CreateVariationAttribute,
  UpdateVariationAttribute,
  VariationAttribute,
} from '../../../models/variation-attribute.model';
import { VariationAttributeService } from '../../../services/variation-attribute.service';
import { FormErrorComponent } from '../../../../shared/components/form-error/form-error.component';
import { FormServerErrorComponent } from '../../../../shared/components/form-server-error/form-server-error.component';
import { ApiResult } from '../../../../shared/models/api-result.model';
import { Observable } from 'rxjs';
import { FormGroupService } from '../../../../shared/services/form-group.service';
import { ToolbarModule } from "primeng/toolbar";

interface DropdownOption {
  label: string;
  value: string | number;
}

@Component({
  selector: 'app-variation-attribute-create-or-update',
  imports: [
    ReactiveFormsModule,
    BreadcrumbModule,
    ButtonModule,
    InputTextModule,
    SelectModule,
    ToggleSwitchModule,
    ProgressSpinnerModule,
    RouterLink,
    FormErrorComponent,
    FormServerErrorComponent,
    RouterLink,
    ToolbarModule
],
  templateUrl: './variation-attribute-create-or-update.component.html',
})
export class VariationAttributeCreateOrUpdateComponent implements OnInit {
  private fb = inject(FormBuilder);
  private activatedRoute = inject(ActivatedRoute);
  private router = inject(Router);
  private messageService = inject(MessageService);
  private variationAttributeService = inject(VariationAttributeService);
  private formGroupService = inject(FormGroupService);

  formGroup: FormGroup = this.fb.group({
    id: [null],
    name: [null, [Validators.required, Validators.maxLength(255)]],
    description: [null, [Validators.maxLength(255)]],
    isRequired: [false, [Validators.required]],
    isActive: [true],
  });

  loading = signal(false);

  variationAttributeId = signal<string | null>(null);

  ngOnInit(): void {
    const id = this.activatedRoute.snapshot.paramMap.get('id');
    if (id) {
      this.variationAttributeId.set(id);
      this.loadData(id);
    }
  }

  loadData(id: string): void {
    this.loading.set(true);

    this.variationAttributeService.getById(id).subscribe({
      next: (response: ApiResult) => {
        this.loading.set(false);

        const model = response.data as VariationAttribute;
        this.formGroup.patchValue(model);
      },
      error: (e) => {
        this.loading.set(false);

        if (e instanceof HttpErrorResponse) {
          this.messageService.add({
            severity: 'error',
            summary: 'Falha',
            detail: 'Ocorreu um erro ao carregar o atributo',
            life: 3000,
          });
        }
      },
    });
  }

  onSubmit(): void {
    if (!this.formGroup.valid) {
      Object.keys(this.formGroup.controls).forEach((key) => {
        const control = this.formGroup.get(key);
        control?.markAsTouched();
      });
      return;
    }

    const id = this.variationAttributeId();
    const body = this.formGroup.getRawValue();

    this.loading.set(true);

    let request$: Observable<ApiResult>;

    if (id) {
      request$ = this.variationAttributeService.update(
        id,
        body as UpdateVariationAttribute
      );
    } else {
      delete body.id;
      request$ = this.variationAttributeService.create(
        body as CreateVariationAttribute
      );
    }

    request$.subscribe({
      next: () => {
        this.loading.set(false);

        this.messageService.add({
          severity: 'success',
          summary: 'Sucesso',
          detail: 'Atributo salvo com sucesso',
          life: 2000,
        });

        this.router.navigate(['/variation-attributes']);
      },
      error: (e) => {
        this.loading.set(false);

        if (e instanceof HttpErrorResponse) {
          if ([400, 422].includes(e.status)) {
            if (
              (e.error.message &&
                (e.error.errors || e.error.errors.length > 0)) ||
              (!e.error.message &&
                (e.error.errors || e.error.errors.length > 0))
            ) {
              this.formGroupService.addServerErrors(
                e.error.errors,
                this.formGroup
              );
            } else if (
              e.error.message &&
              (!e.error.errors || e.error.errors.length == 0)
            ) {
              this.messageService.add({
                severity: 'warn',
                summary: 'Atenção',
                detail: e.error.message,
                life: 3000,
              });
            }
          } else {
            this.messageService.add({
              severity: 'error',
              summary: 'Falha',
              detail: 'Ocorreu um erro ao salvar o atributo',
              life: 3000,
            });
          }
        }
      },
    });
  }
}
